import React, { useEffect } from 'react'
import { intersection, pick } from 'lodash'
import {
  context,
  ProductDetail,
  FullHeightRow,
  SpacedCol,
  FullHeightGrid,
  i18n,
  connectRequest,
  productUtils,
} from '@tools/wr-catalog-base'
import { submitProposal } from '@utils/proposals/submit-proposal'
import { renderPrice } from '@utils/product-utils'
import { trackPageView } from '@utils/tracking'
import DocTitle from '@components/DocTitle'
import Cart from './Cart'
import AddToSOW from './AddToSOW'
import overrides from './overrides'

import { StyledProductDetail } from './styles'
import { isPartnerExperience } from '@utils/isPartnerExperience'
import { CopyPartnerLinkButton } from './CopyPartnerLinkButton'

export const ProductViewContext = React.createContext({})

const NullElement = () => null

function ProductView(props) {
  const {
    state: { cart },
    actions: { refreshCartAddOns, resetSelectedDocumentsIds },
  } = context

  useEffect(() => {
    const bodyEl = window.document.getElementsByTagName('body')[0]
    bodyEl.className = 'product-page'

    resetSelectedDocumentsIds()
  }, [])

  const { match, categoriesFetch } = props
  const { productId } = match.params
  const partnerItems = context.state.partnerCatalogItemsInfo

  // Partner portal users shouldn't be able to access non-partner items through URL
  const isItemInCatalog = partnerItems.some((p) => p.id === productId)
  if (partnerItems.length && isPartnerExperience() && !isItemInCatalog) {
    context.history.push('/')
  }

  const cartProduct = cart.find((item) => item.id === productId)

  const isVRD = categoriesFetch.value?.find((c) => c.name === 'VRDs')
  const showButton = productId && !isVRD

  const isCartAddonMode = cart?.[0]?.categories?.find((c) => c.name === 'Success Paths')
  const isSuccessPath = categoriesFetch.value?.find((c) => c.name === 'Success Paths')
  const disableButton = isCartAddonMode && isSuccessPath && cartProduct?.id !== productId

  const AddToSOWComponent = showButton ? (disableButton ? AddToSOW.Disabled : AddToSOW) : NullElement

  return (
    <ProductViewContext.Provider value={{ isSuccessPath }}>
      <StyledProductDetail>
        <FullHeightGrid fluid>
          <FullHeightRow middle="xs">
            <SpacedCol xs={12}>
              <ProductDetail
                productId={productId}
                bundleId={match.params.bundleId}
                onSubmit={submitProposal}
                AddToSOW={AddToSOWComponent}
                addButtonLabel={i18n('productDetails.addToSOW')}
                addedButtonLabel={i18n('productDetails.addedToSOW')}
                asPdf
                showPrice
                showDescription={false}
                Cart={Cart}
                overrides={overrides}
                initialValues={(cartProduct || {}).values}
                onChange={(values, product, prevFormik) => {
                  if (!cartProduct || !prevFormik) return
                  const prevValues = prevFormik.values
                  cartProduct.values = values
                  cartProduct.rows = productUtils.mergeRowsValues(values, {
                    ...cartProduct,
                    rows: cartProduct.allRows,
                  }).rows
                  const changedKeys = Object.keys(values).filter((key) => {
                    const val = values[key]
                    const prevVal = prevValues[key]
                    if (Array.isArray(val) && Array.isArray(prevVal)) {
                      return val.length !== prevVal.length || val.length !== intersection(val, prevVal).length
                    }
                    return val !== prevVal
                  })
                  const valueChanges = pick(values, changedKeys)
                  refreshCartAddOns(cartProduct, valueChanges)
                }}
                showBackButton
                renderPrice={renderPrice}
                renderCardTitle={(options) => {
                  const partnerHost = context.constants.client.partnerCatalogHost
                  const partnerUrl = partnerHost + window.location.pathname
                  const isPartnerItem = !!partnerItems.find((p) => p.id === productId)

                  return (
                    <>
                      {options.defaultRender()}
                      {isPartnerItem && partnerHost && <CopyPartnerLinkButton partnerUrl={partnerUrl} />}
                    </>
                  )
                }}
              >
                {(renderedParts, { product }) => {
                  return (
                    <>
                      <DocTitle title={`PTC - ${product.name}`} onChange={trackPageView} />
                      {renderedParts.feedback}
                      {renderedParts.backButton}
                      {renderedParts.renderFullMainSection(renderedParts)}
                      {renderedParts.noProducts}
                    </>
                  )
                }}
              </ProductDetail>
            </SpacedCol>
          </FullHeightRow>
        </FullHeightGrid>
      </StyledProductDetail>
    </ProductViewContext.Provider>
  )
}

export default connectRequest((props) => {
  const { productId } = props.match.params
  if (!productId) {
    return {
      categoriesFetch: {
        value: [],
      },
    }
  }

  return {
    categoriesFetch: {
      url: `${context.constants.appHost}/api/v1/service-catalogs/products/search?getCategories=true`,
      method: 'POST',
      body: JSON.stringify({ data: { id: productId } }),
      comparison: productId,
      then: ({ data }) => {
        return { value: data[0].categories }
      },
    },
  }
})(ProductView)
